export interface PointBalanceEntry {
  point: number
  expireTime: number
}

export interface PointBalanceBucket extends PointBalanceEntry {
  text?: string
  list?: readonly PointBalanceEntry[]
}

export interface PointBalance {
  total?: PointBalanceBucket
  general?: PointBalanceBucket
  image?: PointBalanceBucket
  recharge?: PointBalanceBucket
  subscribe?: PointBalanceBucket
  other?: PointBalanceBucket
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  return value as Record<string, unknown>
}

function readNumber(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return null
  }

  for (const key of keys) {
    const value = record[key]

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }

    if (typeof value === 'string') {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
  }

  return null
}

function readString(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return null
  }

  for (const key of keys) {
    const value = record[key]

    if (typeof value === 'string') {
      return value
    }
  }

  return null
}

function normalizePointBalanceEntry(value: unknown) {
  const record = asRecord(value)

  if (!record) {
    return undefined
  }

  const point = readNumber(record, ['point'])
  const expireTime = readNumber(record, ['expire_time', 'expireTime'])

  if (point === null && expireTime === null) {
    return undefined
  }

  return {
    point: point ?? 0,
    expireTime: expireTime ?? 0,
  } satisfies PointBalanceEntry
}

function normalizePointBalanceList(value: unknown) {
  if (!Array.isArray(value)) {
    return undefined
  }

  const list = value
    .map((item) => normalizePointBalanceEntry(item))
    .filter((item): item is PointBalanceEntry => Boolean(item))

  return list.length > 0 ? list : undefined
}

function normalizePointBalanceBucket(value: unknown) {
  const record = asRecord(value)
  const baseEntry = normalizePointBalanceEntry(value)

  if (!baseEntry || !record) {
    return baseEntry
  }

  const list = normalizePointBalanceList(record.list)
  const text = readString(record, ['text']) || undefined

  if (!list && !text) {
    return baseEntry
  }

  return {
    ...baseEntry,
    ...(text ? { text } : {}),
    list,
  } satisfies PointBalanceBucket
}

function mergePointBalanceLists(...lists: Array<readonly PointBalanceEntry[] | undefined>) {
  const validItems = lists.flatMap((list) => list ?? [])

  return validItems.length > 0 ? validItems : undefined
}

function resolveNearestExpireTime(buckets: readonly PointBalanceBucket[]) {
  const activeBuckets = buckets.filter((bucket) => bucket.point > 0)

  if (activeBuckets.length === 0) {
    return 0
  }

  const positiveExpireTimes = activeBuckets
    .map((bucket) => bucket.expireTime)
    .filter((expireTime) => expireTime > 0)

  if (positiveExpireTimes.length > 0) {
    return Math.min(...positiveExpireTimes)
  }

  if (activeBuckets.some((bucket) => bucket.expireTime === -1)) {
    return -1
  }

  return 0
}

function mergePointBalanceBuckets(...buckets: Array<PointBalanceBucket | undefined>) {
  const validBuckets = buckets.filter((bucket): bucket is PointBalanceBucket => Boolean(bucket))
  const mergedList = mergePointBalanceLists(...validBuckets.map((bucket) => bucket.list))
  const mergedText =
    validBuckets
      .map((bucket) => bucket.text?.trim())
      .filter((text): text is string => Boolean(text))
      .join('\n') || undefined

  if (validBuckets.length === 0) {
    return undefined
  }

  return {
    point: validBuckets.reduce((sum, bucket) => sum + bucket.point, 0),
    expireTime: resolveNearestExpireTime(validBuckets),
    ...(mergedText ? { text: mergedText } : {}),
    ...(mergedList ? { list: mergedList } : {}),
  } satisfies PointBalanceBucket
}

export function normalizePointBalance(value: unknown) {
  const record = asRecord(value)

  if (!record) {
    return undefined
  }

  const total = normalizePointBalanceBucket(record.total)
  const general =
    normalizePointBalanceBucket(record.general) ??
    mergePointBalanceBuckets(
      normalizePointBalanceBucket(record.recharge),
      normalizePointBalanceBucket(record.subscribe),
      normalizePointBalanceBucket(record.other),
    )
  const image = normalizePointBalanceBucket(record.image)
  const recharge = normalizePointBalanceBucket(record.recharge)
  const subscribe = normalizePointBalanceBucket(record.subscribe)
  const other = normalizePointBalanceBucket(record.other)

  if (!total && !general && !image && !recharge && !subscribe && !other) {
    return undefined
  }

  return {
    ...(total ? { total } : {}),
    ...(general ? { general } : {}),
    ...(image ? { image } : {}),
    ...(recharge ? { recharge } : {}),
    ...(subscribe ? { subscribe } : {}),
    ...(other ? { other } : {}),
  } satisfies PointBalance
}

export function getPointBalanceTotalPoint(pointBalance: PointBalance | null | undefined) {
  if (!pointBalance) {
    return null
  }

  if (pointBalance.total) {
    return pointBalance.total.point
  }

  if (pointBalance.general) {
    const bucketPoints = [
      pointBalance.general.point,
      pointBalance.image?.point,
    ].filter((value): value is number => typeof value === 'number')

    if (bucketPoints.length === 0) {
      return null
    }

    return bucketPoints.reduce((sum, value) => sum + value, 0)
  }

  const bucketPoints = [
    pointBalance.recharge?.point,
    pointBalance.subscribe?.point,
    pointBalance.image?.point,
    pointBalance.other?.point,
  ].filter((value): value is number => typeof value === 'number')

  if (bucketPoints.length === 0) {
    return null
  }

  return bucketPoints.reduce((sum, value) => sum + value, 0)
}
